<template>
      <div class="pt30 mr70 ml70">
        <section class="card">
          <page-title
              :title="$t('articleTracker.title')"
              :path="''"
              @open="showQueryInfoModal = !showQueryInfoModal"
          />
          <div v-if="errors.invalidUrls.length || errors.limit" class="links-error-container text-sm error-msgs">
            <div v-if="errors.invalidUrls.length">
              <p>{{$t('articleTracker.form.wrongFormat')}}</p>
              <p>{{$t('articleTracker.form.toCorrect')}}</p>
              <ul>
                <li class="text-danger" v-for="(url, index) in errors.invalidUrls" :key="`url-${index}`">{{ url }}</li>
              </ul>
            </div>

            <div v-if="errors.limit">
              <p class="text-danger">{{$t('articleTracker.form.maxError', { max: maxTrackerUrls })}}</p>
            </div>
          </div>

          <div class="form-container">
            <div class="multiselect-wrapper">
              <label for="multiselect-projects" class="text-gray">
                {{$t('articleTracker.form.projectLabel')}}
              </label>
              <multi-select 
                v-model="project"
                id="multiselect-projects"
                class="search-type-select"
                track-by="value"
                label="label"
                :options="options"
                :show-no-results="false" 
                :searchable="true"
                :loading="projectByNameIsBusy"
                @search-change="debouncedGetOptions">
                <template slot="singleLabel" slot-scope="{ option }">{{option.label.split($t('articleTracker.form.projectId'))[0]}}</template>
                <template #option="{ option }">
                  <div>
                    {{option.label.split($t('articleTracker.form.projectId'))[0]}}
                    <br>
                    <small>{{$t('articleTracker.form.client')}} {{ option.client_name }} | {{$t('articleTracker.form.clientId')}} {{ option.client_id }} | {{$t('articleTracker.form.projectId')}} {{ option.value }}</small>
                  </div>
                </template>
                <template slot="selection" slot-scope="{ values }">
                  <div class="select-values" v-if="values.length">
                    <span v-for="(value, i) in values" :key="value.value">{{`${value.label.split($t('articleTracker.form.projectId'))[0]}${values.length -1 != i ? ', ' : ''}`}}</span>
                  </div>
                </template>
                <span
                  v-if="project"
                  slot="clear"
                  class="clear-select"
                  @click="clearProjectAndOptions"
                >
                  <i class="fas fa-times"></i>
                </span>
                <span slot="noOptions">
                  {{$t('articleTracker.form.multiselectEmpty')}}
                </span>
              </multi-select>
            </div>
            <form-renderer
            formWrapperClass = 'null'
            :model="model"
            :fields="inputFields"
            :showMessageNotifications="false"
            :validateFormTrigger="false"
            :show-submit="false"
            @updateModel="(value, field) => updateModel(value, field)"
            />
            <div class="mt10 flex justify-end">
              <button @click="onSubmit" :disabled="model.article_tracker_search.length > 0 ? false : true" class="button button-save">{{$t('articleTracker.form.search')}}</button>
            </div>
          </div>
        </section>
        <div>
          <div class="loader" v-if="articlesByUrlsIsBusy">
            <loader />
          </div>
          <div v-else style="display: flex; flex-direction: column;" class="data-table-card flex">
            <div class="actions" v-if="(tableData && typeof tableData.body !== 'undefined' && tableData.body.length > 0) || originalArticlesByUrls">              
              <multi-select 
                v-model="displayResultFilter"
                track-by="value"
                label="label"
                :options="displayResultsOptions"
                :preselect-first="true"
                :allow-empty="false"
              />
              <div class="flex items-center pl10">
                <span>{{ totalResults }} / {{ this.articleTrackerSearch.length }} {{$t('articleTracker.trackerTable.articlesFound')}}</span>
                <div class="link-pointer ml20 export-btn">
                  <i class="far fa-download text-sm"> </i>
                  <span class="button-export" @click="exportIsBusy ? false : exportArticles(tableData.body)">
                    {{$t('articleTracker.form.export')}}
                  </span>
                </div>
              </div>
            </div>
            <DataTable ref="table" :data="tableData" />
          </div>
        </div>
        <article-tracker-modal v-if="showQueryInfoModal" @closeModal="toggleShowQueryInfoModal" />
      </div>  
</template>
  
<script>
  import DataTable from '../components/datatable.vue'
  import PageTitle from '../components/outlets/Partials/PageTitle';
  import formRenderer from '../components/form-renderer.vue'
  import articleTrackerModal from '../components/article-tracker/info-modal.vue'
  import Loader from '../components/loader';
  import multiSelect from 'vue-multiselect';
  import debounce from 'lodash/debounce'
  import moment from 'moment';
  import { TRACKER_FILTERS, MAX_TRACKER_URLS } from '../config/const'
  import { INSIGHT_HOST, BODHI_HOST, CRAWLER_HOST } from '../config/app-const';
  import { mapState, mapActions } from 'vuex';
  import axios from 'axios'
  import i18n from '../i18n';



export default {
  name: 'ArticleTracker',
  components: {
    formRenderer,
    DataTable,
    Loader,
    multiSelect,
    articleTrackerModal,
    PageTitle,
  },
  data() {
    return {
      showQueryInfoModal: false,
      maxTrackerUrls: MAX_TRACKER_URLS,
      debouncedGetProjects: null,
      options: [],
      project: null,
      projectDetails:null,
      tableData: {},
      originalTableData:{},
      displayResultFilter: {value:TRACKER_FILTERS.EXACT_MATCH, label:this.$i18n.t('articleTracker.trackerTable.displayFilter.exactMatch')},
      displayResultsOptions: [
        {value: TRACKER_FILTERS.EXACT_MATCH, label:this.$i18n.t('articleTracker.trackerTable.displayFilter.exactMatch')},
        {value: TRACKER_FILTERS.ALL_RESULTS, label:this.$i18n.t('articleTracker.trackerTable.displayFilter.all')},
        {value: TRACKER_FILTERS.FOUND, label:this.$i18n.t('articleTracker.trackerTable.displayFilter.found')},
        {value: TRACKER_FILTERS.NOT_FOUND, label:this.$i18n.t('articleTracker.trackerTable.displayFilter.notFound')},
      ],
      cancelTokenSource: null,
      inputFields: ['article_tracker_period', 'article_tracker_search'],
      model: {
        article_tracker_period: [
          moment().local().subtract(92, "days").startOf('day').toDate(), // default is 3 month on B.E for elasticsearch
          moment().local().toDate()
        ],
        article_tracker_search: [],
      },
      articleTrackerSearch:[],
      errors: {
        invalidUrls: [],
        limit: false
      },
    }
  },
  methods: {
    ...mapActions({
      getArticlesByUrls: "articlesByUrls/getArticlesByUrls",
      resetArticlesByFilter: "articlesByUrls/resetArticlesByFilter",
      getAllProjectsByName: "articlesByUrls/getAllProjectsByName",
      exportArticlesFound: "articlesByUrls/exportArticlesFound"
    }),
    clearProjectAndOptions() {
      this.project = null;
    },
    getInvalidUrls(articleTrackerSearch) {
      let invalidUrls = [];

      articleTrackerSearch.forEach(url => {
        if (!url.match(/^(http|https):\/\//)) {
          invalidUrls.push(url);
          return;
        }

        try {
          const urlObj = new URL(url);
          if (!urlObj) {
            invalidUrls.push(url);
          }
        } catch (_) {
          invalidUrls.push(url);
        }
      });

      return invalidUrls;
    },
    toggleShowQueryInfoModal(open) {
      this.showQueryInfoModal = open;
    },
    fetchArticles(urls, project_id, start_date, end_date) {
      let payload = {
        urls,
        project_id,
        start_date,
        end_date
      }
      this.getArticlesByUrls(payload);
    },
    exportArticles(articles) {
      let payload = {
        articles
      }
      this.exportArticlesFound(payload);
    },
    updateModel(newValue, field) {
      if (!field) return;
      this.model[field.name] = newValue[field.name];
    },
    onSubmit() {
      this.displayResultFilter =  {value:"exactMatch", label:this.$i18n.t('articleTracker.trackerTable.displayFilter.exactMatch')};
      let projectId = this.project && typeof this.project === 'object' ? this.project.value : this.project;
      this.projectDetails = this.project
      this.articleTrackerSearch = [...new Set(this.model.article_tracker_search.trim().split('\n').filter(url => url.trim()))];
      let invalidUrls = this.getInvalidUrls(this.articleTrackerSearch);
      if (this.errors) {
        this.errors = {
          invalidUrls: [],
          limit: false
        }
        if (invalidUrls.length > 0) {
          this.errors.invalidUrls = invalidUrls;
          return;
        }
        if (this.articleTrackerSearch.length > MAX_TRACKER_URLS) {
          this.errors.limit = true;
          return;
        }
      }

      this.fetchArticles(this.articleTrackerSearch, projectId, this.model.article_tracker_period[0], this.model.article_tracker_period[1]);
    },
    fetchDebouncedProjects: debounce(function (project_name) {
      if (this.cancelTokenSource) {
        this.cancelTokenSource.cancel("Cancelled the previous request");
      }
      this.cancelTokenSource = axios.CancelToken.source();

      let payload = {
        project_name,
        cancelToken: this.cancelTokenSource.token
      }
      this.getAllProjectsByName(payload);

    }, 300),
    debouncedGetOptions(project_name) {
      if (project_name && typeof project_name === 'string' && project_name.trim().length > 0) {
        this.fetchDebouncedProjects(project_name);
      }
    },
    filterTableData(filter, project) {
      let payload = {
        filter,
        project
      }
      this.articlesByUrls && this.resetArticlesByFilter(payload)
    },
    checkProjAvailable(project, variants) {
      if(!project) return
      return variants.some(variant => {
        return variant.insight?.some(insight => insight.project_id === project.value);
      });
    }
  },
  computed: {
    ...mapState({
      articlesByUrls: state => state.articlesByUrls.articlesByUrls.data,
      originalArticlesByUrls: state => state.articlesByUrls.originalArticlesByUrls.data,
      articlesByUrlsIsBusy: state => state.articlesByUrls.articlesByUrls.isBusy,
      projectByName: state => state.articlesByUrls.projectsByName.data,
      projectByNameIsBusy: state => state.articlesByUrls.projectsByName.isBusy,
      exportIsBusy: state => state.articlesByUrls.articlesXlsx.isBusy,
    }),
    totalResults() {
      let totalUrlsWithResults = 0;

      for (let url of this.articlesByUrls.data) {
        let hasResult = false;

        for (let variant of url.variants) {
          if (this.displayResultFilter.value === "not found") {
            if (this.projectDetails?.value) {
              if (variant.insight !== null || variant.bodhi !== null) {
                hasResult = true;
                break;
              }
            } else {
              if (variant.insight === null && variant.bodhi === null) {
                hasResult = true;
                break;
              }
            }
          } else {
            if ((variant.insight?.length > 0 || variant.bodhi?.length > 0)) {
              if (this.projectDetails?.value && this.displayResultFilter.value !== "exact match") {
                for (let insightItem of variant.insight) {
                  if (insightItem.project_id === this.projectDetails.value) {
                    hasResult = true;
                    break;
                  }
                }
                if (hasResult) {
                  break;
                }
              } else {
                hasResult = true;
                break;
              }
            }
          }
        }
        if (hasResult) {
          totalUrlsWithResults += 1;
        }
      }
      return totalUrlsWithResults;
    }
  },
  watch: {
    articlesByUrls: {
      handler(value) {
        if (value && value.data) {
          const body = value.data;
          this.tableData = {
            body,
            columns: [
              {
                key: 'original_url',
                label: this.$i18n.t('articleTracker.trackerTable.variants'),
                render: (row) => {
                  return (
                    <div
                      class="flex original-url"
                    >
                      <p class='index-dash'>{row.index + 1} -</p>
                      <p>{row.original_url.length > 200 ? row.original_url.substring(0, 200) + '...' : row.original_url}</p>
                    </div>
                  )
                }
              },
              {
                key: 'variants',
                label: this.$i18n.t('articleTracker.trackerTable.allVariants'),
                render: (row) => {
                  return (
                    <div>
                      {row.variants.map((variant, innerIndex) => (
                        <div class={'flex flex-no-wrap relative'}>
                          <span class={'block custom-dot'}></span>
                          <a class={'inner-block'} target='_blank' href={variant.url} key={`inner-${innerIndex}`}>
                          {variant.url.length > 200 ? variant.url.substring(0, 200) + '...' : variant.url}
                          </a>
                        </div>
                      ))}
                    </div>    
                  )
                }
              },
              {
                key: 'bodhi',
                label: this.$i18n.t('articleTracker.trackerTable.bodhiIds'),
                render: (row) => {
                  return (
                    row.variants.map((variant, outerIndex) => (
                      <div key={'outer-' + outerIndex}>
                        {variant.bodhi && variant.bodhi.length > 0 ? 
                          variant.bodhi.map((bodhi, innerIndex) => (
                            <a class={'block'} target='_blank' href={`${BODHI_HOST}/articles/${bodhi.id}/edit`} key={'inner-' + innerIndex}>
                              {bodhi.id}
                            </a>
                          ))
                          : <span>{this.$i18n.t('articleTracker.trackerTable.notFound')}</span>}
                      </div>
                    ))
                  )
                }
              },
              {
                key: 'provider',
                label: this.$i18n.t('articleTracker.trackerTable.provider'),
                render: (row) => {
                  return (
                    row.variants.map((variant, outerIndex) => (
                      <div key={'outer-' + outerIndex}>
                        {variant.bodhi && variant.bodhi.length > 0 ? 
                          variant.bodhi.map((bodhi, innerIndex) => (
                            <span class={'block'} key={'inner-' + innerIndex}>
                              {bodhi.provider === 'bodhi' ? 
                                this.$i18n.t('articleTracker.trackerTable.manualUpload') 
                                : (bodhi.provider === 'lexis_nexis_manual' ? 
                                    this.$i18n.t('articleTracker.trackerTable.lexisNexis') 
                                    : bodhi.provider)
                              }
                            </span>
                          ))
                          : <span>{this.$i18n.t('articleTracker.trackerTable.notFound')}</span>}
                      </div>
                    ))
                  )
                }
              },
              {
                key: 'insight',
                label: this.$i18n.t('articleTracker.trackerTable.insightIds'),
                render: (row) => {
                  return (
                    <div>
                      {row.variants.map((variant, outerIndex) => (
                        <div key={'outer-' + outerIndex}>
                          {variant.insight && variant.insight.length > 0 ? 
                            variant.insight.map((insight, innerIndex) => (
                              insight.insight_id ? (
                                <div class={'flex flex-no-wrap'} key={'inner-' + innerIndex}>
                                  <span class={'block custom-dot'}></span>
                                  <a class='block' target='_blank' href={`${INSIGHT_HOST}/a/${insight.insight_id}`}>
                                    {insight.project_name} - ({insight.client_name})
                                  </a>
                                </div>
                              ) : <span>{this.$i18n.t('articleTracker.trackerTable.notFound')}</span>
                            ))
                          : <span>{this.$i18n.t('articleTracker.trackerTable.notFound')}</span>}
                        </div>
                      ))}
                      {!this.checkProjAvailable(this.projectDetails, row.variants) && this.projectDetails && (
                        <span class="text-danger">
                          {this.projectDetails.label.split(this.$i18n.t('articleTracker.form.projectId'))[0]} {this.$i18n.t('articleTracker.trackerTable.noMatch')}
                        </span>
                      )}
                    </div>
                  );
                }
              },
              {
                key: 'crawler_id',
                label: this.$i18n.t('articleTracker.trackerTable.crawlerIds'),
                render: (row) => {
                  return (
                    row.variants.map((variant, outerIndex) => (
                      <div class="flex flex-col" key={'outer-' + outerIndex}>
                        {variant.crawler_id && variant.crawler_id.length > 0 ? 
                          variant.crawler_id.map((crawler, innerIndex) => (
                            <a target='_blank' href={`${CRAWLER_HOST}/log/${crawler}`} key={'inner-' + innerIndex}>
                              {crawler}
                            </a>
                          ))
                          : <span>{this.$i18n.t('articleTracker.trackerTable.notFound')}</span>}
                      </div>
                    ))
                  )
                }
              },
              {
                key: 'created_at',
                label: this.$i18n.t('articleTracker.trackerTable.createdAt'),
                render: (row) => {
                  return (
                    row.variants.map((variant, outerIndex) => (
                      <div key={'outer-' + outerIndex}>
                        {variant.bodhi && variant.bodhi.length > 0 ? 
                          variant.bodhi.map((bodhi, innerIndex) => (
                            <div key={'inner-' + innerIndex}>
                              {moment.utc(bodhi.created_at).format('YYYY-MM-DD hh:mm A [UTC]')}
                            </div>
                          ))
                          : <span>{this.$i18n.t('articleTracker.trackerTable.notFound')}</span>}
                      </div>
                    ))
                  )
                }
              },
              {
                key: 'published_at',
                label: this.$i18n.t('articleTracker.trackerTable.publishedAt'),
                render: (row) => {
                  return (
                    row.variants.map((variant, outerIndex) => (
                      <div key={'outer-' + outerIndex}>
                        {variant.bodhi && variant.bodhi.length > 0 ? 
                          variant.bodhi.map((bodhi, innerIndex) => (
                            <div key={'inner-' + innerIndex}>
                              {moment.utc(bodhi.published_at).format('YYYY-MM-DD hh:mm A [UTC]')}
                            </div>
                          ))
                          : <span>{this.$i18n.t('articleTracker.trackerTable.notFound')}</span>}
                      </div>
                    ))
                  )
                }
              },
            ],
          }
          this.originalTableData = this.tableData;
        }
      },
      deep: true,
      immediate: true,
    },
    projectByName: {
      handler(value) {
        this.options = value.data.map(project => ({ value: project.id, label: `${project.name} ${i18n.t('articleTracker.form.projectId')} ${project.id}` , client_name: project.client_name, client_id: project.client_id}));
      }
    },
    displayResultFilter(newVal) {
      this.filterTableData(newVal.value, this.projectDetails?.value || null);
    }
  }
}
</script>

<style lang="scss" scoped>
  @import "../styles/components/_article_tracker.scss";
</style>